import { styled } from '@linaria/react'
import React, { FC } from 'react'
import { FormattedMessage } from 'react-intl'
import { colors } from '../styles/colors'
import { TitleAndSubtitle } from '../ui/TitleAndSubtitle'
import TonIcon from '../svgr/TonIcon'
import ArrowRightUpIcon from '../svgr/ArrowUpRight'
import { Typography } from '../ui/Typography'
import { ButtonRegular } from '../ui/Button'
import { Tabs } from '../ui/Tabs'
import { useBonusStats, useClaimRewards } from '../hooks/useBonusData'
import { formatTon } from '../utils/formatTon'
import { BonusTasksTab } from '../components/BonusTasksTab'
import { BonusReferralsTab } from '../components/BonusReferralsTab'
import { useTonAddress } from '@tonconnect/ui-react'
import { useTelegram } from '../hooks/useTelegram'
import { Loader } from '../ui/Loader'

export const bonusTasksPagePath = '/bonuses/tasks'
export const bonusReferralsPagePath = '/bonuses/referrals'
const MIN_WITHDRAW_AMOUNT_TON = 3

export enum BonusTab {
  Tasks = 'tasks',
  Referrals = 'referrals',
}

export const BonusPage: FC<{ activeTab: BonusTab }> = ({ activeTab }) => {
  const walletAddress = useTonAddress()
  const { tg, isTelegramVersionSupported } = useTelegram()
  const { data: stats, isLoading, error } = useBonusStats()
  const claimMutation = useClaimRewards()

  const handleWithdraw = async () => {
    // Use haptic feedback only if supported
    if (isTelegramVersionSupported(6.1)) {
      tg.HapticFeedback.impactOccurred('medium')
    }

    if (!walletAddress) {
      if (isTelegramVersionSupported(6.1)) {
        tg.showPopup({
          title: '❌ Error',
          message: 'Please connect your wallet to withdraw rewards',
          buttons: [{ type: 'ok' }],
        })
      } else {
        // Fallback for older versions
        alert('❌ Error: Please connect your wallet to withdraw rewards')
      }
      return
    }

    if (
      !stats ||
      parseFloat(formatTon(stats.canWithdraw, 2)) < MIN_WITHDRAW_AMOUNT_TON
    ) {
      if (isTelegramVersionSupported(6.1)) {
        tg.HapticFeedback.notificationOccurred('warning')
        tg.showPopup({
          title: '⚠️ Insufficient Amount',
          message:
            'Minimum 3 TON required for withdrawal. Complete more tasks to earn rewards!',
          buttons: [{ type: 'ok' }],
        })
      } else {
        // Fallback for older versions
        alert(
          '⚠️ Insufficient Amount: Minimum 3 TON required for withdrawal. Complete more tasks to earn rewards!',
        )
      }
      return
    }

    // Show confirmation dialog
    const amount = formatTon(stats.canWithdraw, 2)

    if (isTelegramVersionSupported(6.1)) {
      tg.showConfirm(
        `Withdraw ${amount} TON to your wallet?`,
        async confirmed => {
          if (!confirmed) return

          try {
            tg.HapticFeedback.impactOccurred('heavy')
            await claimMutation.mutateAsync({ walletAddress })
            tg.HapticFeedback.notificationOccurred('success')

            tg.showPopup({
              title: '🎉 Success!',
              message: `Withdrawal request for ${amount} TON submitted successfully! Your TON will arrive shortly.`,
              buttons: [{ type: 'ok' }],
            })
          } catch (err) {
            const errorMessage =
              err instanceof Error ? err.message : 'Failed to submit withdrawal'

            tg.HapticFeedback.notificationOccurred('error')

            tg.showPopup({
              title: '❌ Withdrawal Failed',
              message: errorMessage,
              buttons: [{ type: 'ok' }],
            })
          }
        },
      )
    } else {
      // Fallback for older versions - use browser confirm
      const confirmed = confirm(`Withdraw ${amount} TON to your wallet?`)
      if (!confirmed) return

      try {
        await claimMutation.mutateAsync({ walletAddress })
        alert(
          `🎉 Success! Withdrawal request for ${amount} TON submitted successfully! Your TON will arrive shortly.`,
        )
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to submit withdrawal'
        alert(`❌ Withdrawal Failed: ${errorMessage}`)
      }
    }
  }

  const canWithdrawAmount = stats
    ? parseFloat(formatTon(stats.canWithdraw, 2))
    : 0

  const isClaimRewardAvailable = canWithdrawAmount >= MIN_WITHDRAW_AMOUNT_TON

  return (
    <Container>
      <TitleAndSubtitle
        title={<FormattedMessage id="bonus.title" defaultMessage="Бонусы" />}
        textAlign="center"
      />

      {isLoading && (
        <StatsContainer>
          <StatCard>
            <StatValue fontSize={13} fontWeight={700}>
              <SmallLoader /> <TonIcon />
            </StatValue>

            <Typography
              fontSize={10}
              font="roboto"
              color={colors.textSecondary}
            >
              <FormattedMessage id="bonus.earned" defaultMessage="Заработано" />
            </Typography>
          </StatCard>

          <StatCard>
            <StatValue fontSize={13} fontWeight={700}>
              <SmallLoader /> <TonIcon />
            </StatValue>

            <Typography
              fontSize={10}
              font="roboto"
              color={colors.textSecondary}
            >
              <FormattedMessage
                id="bonus.canWithdraw"
                defaultMessage="Можно вывести"
              />
            </Typography>
          </StatCard>
        </StatsContainer>
      )}

      {walletAddress && stats && !isLoading && !error && (
        <StatsContainer>
          <StatCard>
            <StatValue fontSize={13} fontWeight={700}>
              {formatTon(stats.totalEarned, 2)} <TonIcon />
            </StatValue>

            <Typography
              fontSize={10}
              font="roboto"
              color={colors.textSecondary}
            >
              <FormattedMessage id="bonus.earned" defaultMessage="Заработано" />
            </Typography>
          </StatCard>

          <StatCard>
            <StatValue fontSize={13} fontWeight={700}>
              {formatTon(stats.canWithdraw, 2)} <TonIcon />
              {isClaimRewardAvailable && <StyledWithdrawIcon />}
              {isClaimRewardAvailable && (
                <StyledClaimRewardAbsoluteButton onClick={handleWithdraw} />
              )}
            </StatValue>

            <Typography
              fontSize={10}
              font="roboto"
              color={colors.textSecondary}
            >
              <FormattedMessage
                id="bonus.canWithdraw"
                defaultMessage="Можно вывести"
              />
            </Typography>
          </StatCard>
        </StatsContainer>
      )}

      <Tabs
        activeTabId={activeTab}
        tabs={[
          {
            id: BonusTab.Tasks,

            label: (
              <FormattedMessage id="bonus.tasks" defaultMessage="Задания" />
            ),

            to: bonusTasksPagePath,
          },

          {
            id: BonusTab.Referrals,

            label: (
              <FormattedMessage
                id="bonus.referrals"
                defaultMessage="Рефералы"
              />
            ),

            to: bonusReferralsPagePath,
          },
        ]}
      />

      {activeTab === BonusTab.Tasks && <BonusTasksTab />}
      {activeTab === BonusTab.Referrals && <BonusReferralsTab />}
    </Container>
  )
}

const Container = styled.div`
  display: grid;
  gap: 16px;
  padding-top: 12px;
  width: 100%;
  background: ${colors.surfacePrimary};
  align-content: flex-start;
`

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
`

const StatCard = styled.div`
  display: grid;
  gap: 2px;
  background: ${colors.surfaceSecondary};
  border-radius: 16px;
  padding: 9px 16px;
  align-content: center;
`

const StatValue = styled(Typography)`
  position: relative;
  display: grid;
  grid-auto-flow: column;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  flex-gap: 4px;
`

const StyledWithdrawIcon = styled(ArrowRightUpIcon)`
  width: 20px;
  height: 20px;
`

const SmallLoader = styled(Loader)`
  height: 16px;
  width: 16px;
`

const StyledClaimRewardAbsoluteButton = styled(ButtonRegular)`
  position: absolute;
  inset: 0;
`
